import { IndexDataTable } from '@/components/tables/IndexTable'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import type { PathHelpers } from '@/hooks/usePathHelper'
import type {
  SalesTaxReport,
  SalesTaxReportJurisdiction,
  SalesTaxReportSummary,
} from '@/lib/services/reports/reports.types'
import { ReceiptText } from 'lucide-react'
import { useState } from 'react'
import { REPORT_PIE_CHART_COLORS, ReportCountBadge as CountBadge } from '../-helpers'
import { SalesTaxJurisdictionAside } from './-jurisdiction-aside'
import { JurisdictionTaxPieChart } from './-jurisdiction-pie-chart'
import { buildJurisdictionChartRows } from './-presentation'
import { SalesTaxSummaryCards } from './-summary-cards'
import { getDetailedLedgerColumns, getJurisdictionSummaryColumns } from './-table-columns'

const EMPTY_SUMMARY: SalesTaxReportSummary = {
  gross_taxable_sales: '0.00',
  untaxable_amount: '0.00',
  tax_collected: '0.00',
  transaction_count: 0,
}

type SalesTaxReportResultsProps = {
  report: SalesTaxReport | undefined
  /** The `$orgId` route param, parsed to a number. */
  orgId: number
  path: PathHelpers
}

export function SalesTaxReportResults({ report, orgId, path }: SalesTaxReportResultsProps) {
  const [selectedJurisdiction, setSelectedJurisdiction] =
    useState<SalesTaxReportJurisdiction | null>(null)
  const timezone = report?.filters?.timezone
  const summary = report?.summary ?? EMPTY_SUMMARY
  const jurisdictions = report?.jurisdictions ?? []
  const rows = report?.rows ?? []
  const chartRows = buildJurisdictionChartRows(jurisdictions, REPORT_PIE_CHART_COLORS)
  const chartRowColorByLabel = new Map(chartRows.map((row) => [row.jurisdiction_label, row.fill]))
  const hasReportData = jurisdictions.length > 0 || rows.length > 0

  return (
    <>
      <SalesTaxSummaryCards summary={summary} />

      {hasReportData ? (
        <>
          <Card>
            <CardHeader className="gap-4 md:flex md:flex-row md:items-start md:justify-between">
              <div className="space-y-1.5">
                <CardTitle>Jurisdiction Summary</CardTitle>
                <CardDescription>
                  A breakdown of sales and tax collected by jurisdiction
                </CardDescription>
              </div>
              <CountBadge>
                {jurisdictions.length}{' '}
                {jurisdictions.length === 1 ? 'jurisdiction' : 'jurisdictions'}
              </CountBadge>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)] lg:items-center">
                <JurisdictionTaxPieChart chartRows={chartRows} />
                <IndexDataTable
                  mode="client"
                  data={jurisdictions}
                  columns={getJurisdictionSummaryColumns({
                    chartRowColorByLabel,
                    onOpenJurisdiction: setSelectedJurisdiction,
                  })}
                  getRowId={(row) => row.jurisdiction_label}
                  pagination={false}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardHeader className="gap-4 md:flex md:flex-row md:items-start md:justify-between">
              <div className="space-y-1.5">
                <CardTitle>Orders and refunds</CardTitle>
                <CardDescription>
                  Each sale, refund and cancellation, on the day it happened
                </CardDescription>
              </div>
              <CountBadge>
                {summary.transaction_count}{' '}
                {summary.transaction_count === 1 ? 'transaction' : 'transactions'}
              </CountBadge>
            </CardHeader>
            <CardContent>
              <IndexDataTable
                mode="client"
                pagination
                data={rows}
                columns={getDetailedLedgerColumns({ path, orgId, timezone })}
                getRowId={(row) => row.activity_id}
                maxPageSize={50}
                syncPaginationToUrl
              />
            </CardContent>
          </Card>

          <SalesTaxJurisdictionAside
            open={Boolean(selectedJurisdiction)}
            onOpenChange={(open) => {
              if (!open) {
                setSelectedJurisdiction(null)
              }
            }}
            jurisdiction={selectedJurisdiction}
          />
        </>
      ) : (
        <Card>
          <CardContent className="py-12">
            <EmptySalesTaxState />
          </CardContent>
        </Card>
      )}
    </>
  )
}

function EmptySalesTaxState() {
  return (
    <div className="text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <ReceiptText className="h-8 w-8 text-muted-foreground" />
      </div>
      <Typography variant="h4" tone="muted">
        No sales tax information in the selected date range
      </Typography>
    </div>
  )
}
